import React, { useState, useEffect, useContext } from 'react';
import { Box, Tabs, Tab, Skeleton, Container, Stack } from '@mui/material';
import GreyToGreyDiv from '../../common/grey_grey_div.png';
import GreyToWhiteDiv from '../../common/grey_white_div.png';
import AccountListSummary from './Sections/Summary/AccountListSummary';
import AccountsStatements from './Sections/AccountsStatements/AccountsStatements';
import AccountInvestments from './Sections/AccountInvestments/AccountInvestments';
import { AccountsStatement } from '../../networking/accounts/statements/AccountsStatement';
import { getAccountStatements } from '../../networking/accounts/statements/statements';
import { getAccountDetails } from '../../networking/accounts/details/details';
import { AccountsInvestment } from '../../networking/accounts/investments/AccountsInvestment.types';
import { getAccountInvestments } from '../../networking/accounts/investments/investments';
import { AccountDetails } from '../../networking/accounts/details/AccountDetails';
import { AccountFiltersOptions, getFilters } from '../../networking/accounts/filters/filters';
import { getInstitutionRatings } from '../../networking/accounts/ratings/ratings';
import { InstitutionRating } from '../../networking/accounts/ratings/InstitutionRating';

import { AuthContext } from '../../common/authContext';
import { hasContent } from '../../common/helpers';
import { StringFilterState, stringFilterToArray } from '../../components/StringFilter/StringFilter';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import {
    ACCOUNT_DETAILS_ACCOUNTS_PAGE,
    ACCOUNT_DETAILS_INVESTMENTS_PAGE
} from '../../routing/ROUTES';

const statementsFilterInitialState = {
    institution: {},
    productType: {},
    group: {},
    closedAccount: { 'Closed accounts': { value: true } }
};

const investmentsFilterInitialState = {
    institution: {},
    productType: {},
    matureInvestment: { 'Matured investments': { value: true } }
};

const initialDetails = {
    dateAsOf: '0',
    openAccounts: 0,
    totalBalance: 0,
    maturingInvestments: 0,
    maturingInvestmentsTotal: 0,
    balances: []
};

type PagePathParam = 'investments' | undefined;

const AccountDetailsPage = () => {
    const { authState } = useContext(AuthContext);
    const [isLoading, setIsLoading] = useState(true);
    const [isChartReloading, setIsChartReloading] = useState(false);
    const [investments, setInvestments] = useState<AccountsInvestment[] | {}>({});
    const [statements, setStatements] = useState<AccountsStatement[] | {}>({});
    const [details, setDetails] = useState<AccountDetails | {}>({});
    // Feature: Freeze the summary boxes to represent the global portfolio. 
    // We store the initial unfiltered payload here and never update it again,
    // ensuring the summary metrics don't jump around when filters change.
    const [staticSummary, setStaticSummary] = useState<Omit<AccountDetails, 'balances'> | null>(null);
    const [latestRatings, setLatestRatings] = useState<InstitutionRating[]>([{ "institutionName": "" }]);
    const [accountRatings, setAccountRatings] = useState<InstitutionRating[]>([{ "institutionName": "" }]);
    const [allRatings, setAllRatings] = useState<InstitutionRating[]>([{ "institutionName": "" }]);
    const [statementsFilter, setStatementsFilter] = useState(statementsFilterInitialState);
    const [investmentsFilter, setInvestmentsFilter] = useState(investmentsFilterInitialState);
    const [allAccountsGroupId, setAllAccountsGroupId] = useState<string | null>(null);
    const [initialFiltersLoaded, setInitialFiltersLoaded] = useState(false);
    const { t } = useTranslation();
    const { page } = useParams<{ page: PagePathParam }>();
    const tabState = page ?? 'accounts';

    const updateInvestmentsMatureInvestmentFilter = (newValues: any) => {
        setInvestmentsFilter((currentState) => {
            return {
                ...currentState,
                matureInvestment: { ...newValues }
            };
        });
    };

    const updateInvestmentsInstitutionFilter = (newValues: StringFilterState) => {
        setInvestmentsFilter((currentState) => {
            return {
                ...currentState,
                institution: { ...newValues }
            };
        });
    };

    const updateInvestmentsProductFilter = (newValues: StringFilterState) => {
        setInvestmentsFilter((currentState) => {
            return {
                ...currentState,
                productType: { ...newValues }
            };
        });
    };

    const updateStatementsClosedAccountFilter = (newValues: any) => {
        setStatementsFilter((currentState) => {
            return {
                ...currentState,
                closedAccount: { ...newValues }
            };
        });
    };

    const updateStatementsInstitutionFilter = (newValues: StringFilterState) => {
        setStatementsFilter((currentState) => {
            return {
                ...currentState,
                institution: { ...newValues }
            };
        });
    };

    const updateStatementsProductFilter = (newValues: StringFilterState) => {
        setStatementsFilter((currentState) => {
            return {
                ...currentState,
                productType: { ...newValues }
            };
        });
    };

    const updateStatementsGroupFilter = (newValues: StringFilterState) => {
        setStatementsFilter((currentState) => {
            return {
                ...currentState,
                group: { ...newValues }
            };
        });
    };

    const setInitialFilters = (filtersResponse: AccountFiltersOptions) => {
        let productTypeInitialState: StringFilterState = {};
        let institutionInitialState: StringFilterState = {};
        let groupInitialState: StringFilterState = {};

        filtersResponse.institutions.forEach((inst) => {
            if (inst !== null && inst !== undefined) {
                institutionInitialState[inst] = { value: true, label: inst };
            }
        });

        filtersResponse.productTypes.forEach((pt) => {
            if (pt !== null && pt !== undefined) {
                productTypeInitialState[pt] = { value: true, label: pt };
            }
        });

        const allAccounts = filtersResponse.accountGroups.find((g) => g.name === 'All Accounts');
        if (allAccounts) {
            setAllAccountsGroupId(allAccounts.id.toString());
        }

        const filteredGroups = filtersResponse.accountGroups.filter((g) => g.name !== 'All Accounts');
        const sortedGroups = [...filteredGroups].sort((a, b) => a.name.localeCompare(b.name));

        sortedGroups.forEach((group) => {
            groupInitialState[group.id] = {
                value: true,
                label: group.name
            };
        });

        setStatementsFilter((currentState) => {
            return {
                ...currentState,
                institution: { ...institutionInitialState },
                productType: { ...productTypeInitialState },
                group: { ...groupInitialState }
            };
        });
    };

    const setInitialInvestmentsFilters = (statementResponse: AccountsInvestment[]) => {
        let productTypeInitialState: StringFilterState = {};
        let institutionInitialState: StringFilterState = {};

        Array.from(statementResponse as AccountsInvestment[]).forEach((statement) => {
            productTypeInitialState = {
                ...productTypeInitialState,
                [statement.accountType]: { value: true }
            };
            institutionInitialState = {
                ...institutionInitialState,
                [statement.institutionName]: { value: true }
            };
        });

        setInvestmentsFilter((currentState) => {
            return {
                ...currentState,
                institution: { ...institutionInitialState },
                productType: { ...productTypeInitialState }
            };
        });
    };

    useEffect(() => {
        if (!initialFiltersLoaded) return;

        const institutionFilterArray = stringFilterToArray(statementsFilter.institution);
        const productTypeFilterArray = stringFilterToArray(statementsFilter.productType);
        let groupFilterArray = stringFilterToArray(statementsFilter.group);

        if (
            allAccountsGroupId &&
            Object.keys(statementsFilter.group).length > 0 &&
            groupFilterArray.length === Object.keys(statementsFilter.group).length
        ) {
            groupFilterArray = [allAccountsGroupId];
        }


        setIsChartReloading(true);
        getAccountDetails(
            authState,
            institutionFilterArray,
            productTypeFilterArray,
            groupFilterArray,
            statementsFilter.closedAccount['Closed accounts'].value
        ).then((accountDetails) => {
            setDetails(accountDetails);
            setStaticSummary((prev) => {
                if (prev) return prev;
                const { balances, ...rest } = accountDetails as AccountDetails;
                return rest;
            });
            getInstitutionRatings(authState).then((allRatings) => {
                setAllRatings(allRatings);
                setLatestRatings(
                    institutionFilterArray.map((bankName) => {
                        let foundRating = allRatings.find((rating) => rating.institutionName == bankName);
                        if (foundRating) return foundRating;
                        else return { institutionName: bankName };
                    })
                );
                setAccountRatings(
                    allRatings.filter((rating) => institutionFilterArray.includes(rating.institutionName))
                );
            });
            setIsLoading(false);
            setIsChartReloading(false);
        });

        getAccountStatements(
            authState,
            0,
            0,
            groupFilterArray,
            institutionFilterArray,
            productTypeFilterArray
        ).then((accountStatements) => {
            setStatements(accountStatements as AccountsStatement[]);
        });
    }, [authState.clientId, authState.bearerToken, statementsFilter, allAccountsGroupId]);
    useEffect(() => {
        getAccountInvestments(authState).then((accountInvestment) => {
            setInvestments(accountInvestment);
            setInitialInvestmentsFilters(accountInvestment as AccountsInvestment[]);
        });

        getFilters(authState).then((filters) => {
            if (filters) {
                setInitialFilters(filters);
            }
            setInitialFiltersLoaded(true);
        });
    }, [authState.clientId, authState.bearerToken]);

    const { balances, ...summary } = details as AccountDetails;
    const finalSummary = staticSummary || summary;

    return (
        <Box
            sx={{
                backgroundColor: '#f2f2f2'
            }}
        >
            <Box sx={{ width: '100%' }}>
                <img src={GreyToGreyDiv} style={{ width: '100%', display: 'block', objectFit: 'cover' }} alt="header-background1" />
            </Box>

            <Container maxWidth="xl">
                <AccountListSummary {...finalSummary} isLoading={isLoading || !hasContent(details)} />
            </Container>

            <Box
                sx={{
                    backgroundColor: '#ffffff',
                    width: '100%'
                }}
            >
                <img src={GreyToWhiteDiv} style={{ width: '100%', display: 'block', objectFit: 'cover' }} alt="header-background2" />
            </Box>

            <Box
                sx={{
                    backgroundColor: '#ffffff',
                    color: 'text.primary',
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%'
                }}
            >
                <Container maxWidth="xl" sx={{ display: 'flex', justifyContent: 'center', borderBottom: '1px solid rgba(121, 121, 121, 0.2)' }}>
                    <Tabs value={tabState} indicatorColor="primary" textColor="inherit">
                        <Tab
                            label={t('accountDetailsPage.accountsListTab')}
                            sx={{
                                textTransform: 'none',
                                fontSize: '32px',
                                fontWeight: '400'
                            }}
                            value={'accounts'}
                            to={ACCOUNT_DETAILS_ACCOUNTS_PAGE}
                            component={Link}
                        />
                        <Tab
                            label={t('accountDetailsPage.investmentsTab')}
                            sx={{
                                textTransform: 'none',
                                fontSize: '32px',
                                fontWeight: '400'
                            }}
                            value={'investments'}
                            to={ACCOUNT_DETAILS_INVESTMENTS_PAGE}
                            component={Link}
                        />
                    </Tabs>
                </Container>
            </Box>

            <Box sx={{ width: '100%', backgroundColor: '#ffffff' }}>
                <Container maxWidth="xl" sx={{ paddingBottom: '32px' }}>
                    {isLoading ? (
                        <Box sx={{ pt: 3 }}>
                            {/* Filters Skeleton */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr 1fr' }, gap: '20px', mb: 5 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1.5 }} />
                                    <Skeleton variant="rounded" height={56} />
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Skeleton variant="text" width="65%" height={24} sx={{ mb: 1.5 }} />
                                    <Skeleton variant="rounded" height={56} />
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Skeleton variant="text" width="45%" height={24} sx={{ mb: 1.5 }} />
                                    <Skeleton variant="rounded" height={56} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5 }}>
                                        <Skeleton variant="rounded" width={24} height={24} sx={{ mr: 1 }} />
                                        <Skeleton variant="text" width="70%" height={24} />
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', mt: '40px' }}>
                                    <Skeleton variant="rounded" height={40} sx={{ mb: 2 }} />
                                    <Skeleton variant="rounded" height={40} />
                                </Box>
                            </Box>

                            {/* Graph Skeleton */}
                            <Skeleton variant="rounded" width="100%" height={380} sx={{ mb: 4 }} />

                            {/* Table Skeleton */}
                            {[...Array(5)].map((_, i) => (
                                <Skeleton key={i} animation="wave" height={65} sx={{ mb: 1 }} />
                            ))}
                        </Box>
                    ) : (
                        <>
                            {page === undefined && Array.isArray(statements) && hasContent(details) && (
                                <AccountsStatements
                                    statements={statements as AccountsStatement[]}
                                    balances={balances}
                                    latestRatings={latestRatings as InstitutionRating[]}
                                    accountRatings={accountRatings as InstitutionRating[]}
                                    allRatings={allRatings as InstitutionRating[]}
                                    dateAsOf={summary.dateAsOf}
                                    institution={statementsFilter.institution}
                                    setInstitution={updateStatementsInstitutionFilter}
                                    productType={statementsFilter.productType}
                                    setProductType={updateStatementsProductFilter}
                                    group={statementsFilter.group}
                                    setGroup={updateStatementsGroupFilter}
                                    closedAccount={statementsFilter.closedAccount}
                                    setClosedAccount={updateStatementsClosedAccountFilter}
                                    reloading={isChartReloading}
                                />
                            )}

                            {page === 'investments' && Array.isArray(investments) && (
                                <AccountInvestments
                                    accountInvestments={investments as AccountsInvestment[]}
                                    institution={investmentsFilter.institution}
                                    productType={investmentsFilter.productType}
                                    matureInvestment={investmentsFilter.matureInvestment}
                                    setInstitution={updateInvestmentsInstitutionFilter}
                                    setProductType={updateInvestmentsProductFilter}
                                    setMatureInvestment={updateInvestmentsMatureInvestmentFilter}
                                />
                            )}
                        </>
                    )}
                </Container>
            </Box>
        </Box >
    );
};

export default AccountDetailsPage;
